import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  CAPTCHA_RESET_EVENT,
  renderRecaptchaWidget,
  setPendingTicket,
  verifyCaptcha,
} from "../Utilities/recaptcha";

// Server tickets live 5 minutes; refresh a little before that so Confirm never
// submits an expired one.
const TICKET_REFRESH_MS = 4 * 60 * 1000;

/**
 * Visible reCAPTCHA v3 step for an Agent Portal action (player creation,
 * recharge, redeem).
 *
 * Renders Google's reCAPTCHA badge inline and verifies as soon as it mounts.
 * Reports "loading" | "verified" | "error" through onStatusChange so the form
 * can keep Confirm disabled until the agent is verified.
 */
export const AgentCaptchaVerification = ({
  action,
  verifiedLabel = "Verified - you can continue",
  onStatusChange,
}) => {
  const containerRef = useRef(null);
  const widgetPromiseRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const runRef = useRef(0);

  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const updateStatus = useCallback(
    (next) => {
      setStatus(next);
      onStatusChange?.(next);
    },
    [onStatusChange]
  );

  const verify = useCallback(async () => {
    // Only the latest run may update state (StrictMode double-mount, Retry
    // clicks, refresh timer).
    const runId = ++runRef.current;
    const isStale = () => !mountedRef.current || runId !== runRef.current;

    clearTimeout(refreshTimerRef.current);
    setPendingTicket(action, null);
    setErrorMessage("");
    updateStatus("loading");

    try {
      // Render the widget once per form; later runs reuse it.
      if (!widgetPromiseRef.current) {
        widgetPromiseRef.current = renderRecaptchaWidget(
          containerRef.current
        ).catch((error) => {
          widgetPromiseRef.current = null;
          throw error;
        });
      }
      const widgetId = await widgetPromiseRef.current;

      const { ticket, enabled } = await verifyCaptcha(widgetId, action);
      if (isStale()) return;

      if (enabled && !ticket) {
        throw new Error("reCAPTCHA verification failed. Please try again.");
      }

      setPendingTicket(action, ticket);
      updateStatus("verified");

      refreshTimerRef.current = setTimeout(() => {
        verify();
      }, TICKET_REFRESH_MS);
    } catch (error) {
      if (isStale()) return;
      setErrorMessage(
        error?.message || "reCAPTCHA verification failed. Please try again."
      );
      updateStatus("error");
    }
  }, [action, updateStatus]);

  useEffect(() => {
    mountedRef.current = true;
    verify();

    const onReset = (event) => {
      if (!event.detail || event.detail === action) verify();
    };
    window.addEventListener(CAPTCHA_RESET_EVENT, onReset);

    return () => {
      mountedRef.current = false;
      clearTimeout(refreshTimerRef.current);
      window.removeEventListener(CAPTCHA_RESET_EVENT, onReset);
      setPendingTicket(action, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 1.5,
        width: "100%",
        border: "1px solid rgba(255, 239, 153, 0.20)",
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {status === "loading" && <CircularProgress size={20} />}
        {status === "verified" && <CheckCircleIcon sx={{ color: "#4caf50" }} />}
        {status === "error" && <ErrorOutlineIcon sx={{ color: "#f44336" }} />}

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: "#FFF" }}>
            {status === "loading" && "Verifying you are human..."}
            {status === "verified" && verifiedLabel}
            {status === "error" && "reCAPTCHA verification failed"}
          </Typography>
          {status === "error" && (
            <Typography variant="caption" sx={{ color: "#f44336" }}>
              {errorMessage}
            </Typography>
          )}
        </Box>

        {status === "error" && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => verify()}
            sx={{ color: "#FFD700", borderColor: "#FFD700", ml: 1 }}
          >
            Retry
          </Button>
        )}
      </Box>

      {/* Google's reCAPTCHA badge is rendered here, inline. */}
      <Box ref={containerRef} />
    </Box>
  );
};
